import Vue, { watchEffect } from 'vue';
import Router from 'vue-router';
import { ROLES } from '@/constants/roles';

import getModulesRoutes from './getModulesRoutes';
import routeHandler from './routeHandler';

import User from '@/utils/user.js';
import { clubsList } from '@/modules/UIDesign/Club/routes';
import { adminEventsModerate } from '@/modules/UIDesign/Admin/routes';
import { uiEventPlan } from '@/modules/EventsPlan/routes';
import { moderatorEventsModerate } from '@/modules/Moderation/routes';

export const defaultMain = {
  path: '/',
  name: 'main',
  meta: {
    title: 'Главная',
    icon: 'mdi-home-outline',
    layout: 'ui-design',
  },
};

const defaultDashboard = {
  ...defaultMain,
  component: () => import('@/modules/Dashboard/views/dashboard.vue'),
  meta: {
    ...defaultMain.meta,
    title: 'Рабочий стол',
    buttonTitle: 'Главная',
    layout: 'ui-design',
  },
};

const userRedesignMain = {
  ...defaultMain,
  component: () => import('@/modules/Dashboard/views/member-dashboard.vue'),
  meta: {
    ...defaultMain.meta,
    title: 'Главная',
    fluidContainer: true,
  },
};

const defaultMainWithEventsList = {
  ...defaultMain,
  component: () => import('@/modules/Events/views/MainEventList.vue'),
  meta: {
    ...defaultMain.meta,
    title: 'Главная',
    hasSearch: true,
    hideTitle: true,
  },
};

const defaultEventsSearch = {
  ...defaultMain,
  component: () => import('@/modules/UIDesign/Events/views/events-search.vue'),
  meta: {
    ...defaultMain.meta,
    title: 'Рабочий стол',
    buttonTitle: 'Главная',
    layout: 'ui-design',
  },
};

const MAINPAGE_COMPONENT_BY_ROLE = {
  [ROLES.organization]: defaultDashboard,
  [ROLES.admin]: {
    ...adminEventsModerate,
    ...defaultMain,
    meta: {
      ...defaultMain.meta,
      buttonTitle: defaultMain.meta?.title,
      title: adminEventsModerate.meta?.title,
    },
  },
  [ROLES.expert]: defaultDashboard,
  [ROLES.operator]: {
    ...uiEventPlan,
    ...defaultMain,
    meta: {
      ...defaultMain.meta,
      buttonTitle: defaultMain.meta?.title,
      title: uiEventPlan.meta?.title,
      new: true,
      uiDesign: true,
      wide: true,
    },
  },
  [ROLES.department]: {
    ...uiEventPlan,
    ...defaultMain,
    meta: {
      ...defaultMain.meta,
      buttonTitle: defaultMain.meta?.title,
      title: uiEventPlan.meta?.title,
      new: true,
      uiDesign: true,
      wide: true,
    },
  },
  [ROLES.moderator]: {
    ...moderatorEventsModerate,
    ...defaultMain,
    meta: {
      ...defaultMain.meta,
      buttonTitle: defaultMain.meta?.title,
      title: moderatorEventsModerate.meta?.title,
    },
  },
  // default for authorized
  default: defaultMainWithEventsList,
  [ROLES.trainer]: {
    ...clubsList,
    ...defaultMain,
    meta: {
      ...clubsList.meta,
    },
  },
  [ROLES.member]: userRedesignMain,
  [ROLES.certificate]: userRedesignMain,
  // default for non-authorized
  guest: {
    ...defaultEventsSearch,
    meta: {
      ...defaultEventsSearch.meta,
      buttonTitle: 'Найди своё мероприятие',
      title: 'Найди своё мероприятие здесь!',
    },
  },
};

export const initRouter = () => {
  Vue.use(Router);

  const router = new Router({
    mode: 'history',
  });

  /**
   * Watch and setup routes by role.
   *
   * It can be work without watchEffect via hard reload page after each authenticate
   */
  watchEffect(() => {
    const user = new User();
    let mainPage = MAINPAGE_COMPONENT_BY_ROLE.guest;
    const { isAuthenticated, role } = user;

    if (isAuthenticated) {
      mainPage = MAINPAGE_COMPONENT_BY_ROLE[role.name] ? MAINPAGE_COMPONENT_BY_ROLE[role.name] : MAINPAGE_COMPONENT_BY_ROLE.default;
    }

    const routes = getModulesRoutes(user);
    routes.push(mainPage);

    const newRouter = new Router({
      mode: 'history',
      routes,
    });
    router.matcher = newRouter.matcher;
  });

  router.beforeEach(routeHandler);
  router.onError((err) => {
    // eslint-disable-next-line
    console.log(err);
    const isChunkError = String(err || '').includes('ChunkLoadError');
    if (isChunkError) {
      // eslint-disable-next-line
      alert('Пожалуйста, перезагрузите страницу, чтобы применить последние обновления в приложении');
    }
  });

  return { router };
};

export default { initRouter };
